import type { Clock } from "./clock";

/** Calendar day as "YYYY-MM-DD" in the user's local calendar. */
export type DateKey = string;

const KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY_MS = 86_400_000;

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function isValidDateKey(key: string): boolean {
  const match = KEY_PATTERN.exec(key);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}

export function isValidTime(value: string): boolean {
  return TIME_PATTERN.test(value);
}

export function parseDateKey(key: DateKey): { year: number; month: number; day: number } {
  if (!isValidDateKey(key)) throw new Error(`Invalid date key: ${key}`);
  return { year: Number(key.slice(0, 4)), month: Number(key.slice(5, 7)), day: Number(key.slice(8, 10)) };
}

export function makeDateKey(year: number, month: number, day: number): DateKey {
  const pad = (n: number, width: number) => n.toString().padStart(width, "0");
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

export function dateKeyInZone(date: Date, timeZone: string): DateKey {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function todayKey(clock: Clock): DateKey {
  return dateKeyInZone(clock.now(), clock.timeZone());
}

function toUtcMs(key: DateKey): number {
  const { year, month, day } = parseDateKey(key);
  return Date.UTC(year, month - 1, day);
}

function fromUtcMs(ms: number): DateKey {
  const d = new Date(ms);
  return makeDateKey(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

export function addDays(key: DateKey, days: number): DateKey {
  return fromUtcMs(toUtcMs(key) + days * DAY_MS);
}

export function diffDays(from: DateKey, to: DateKey): number {
  return Math.round((toUtcMs(to) - toUtcMs(from)) / DAY_MS);
}

export function compareDateKeys(a: DateKey, b: DateKey): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** 0 = Monday … 6 = Sunday. */
export function weekdayMondayFirst(key: DateKey): number {
  return (new Date(toUtcMs(key)).getUTCDay() + 6) % 7;
}

export function startOfWeek(key: DateKey): DateKey {
  return addDays(key, -weekdayMondayFirst(key));
}

export function monthStart(key: DateKey): DateKey {
  const { year, month } = parseDateKey(key);
  return makeDateKey(year, month, 1);
}

export function monthEnd(key: DateKey): DateKey {
  const { year, month } = parseDateKey(key);
  return makeDateKey(year, month, daysInMonth(year, month));
}

export function eachDay(from: DateKey, to: DateKey): DateKey[] {
  const out: DateKey[] = [];
  for (let key = from; key <= to; key = addDays(key, 1)) out.push(key);
  return out;
}
